import { getTreeTeamsContributors } from 'common/utils/contributors.utils';
import orderBy from 'lodash/orderBy';
import { useSelector } from 'react-redux';
import { contributorsSelector } from 'state/selectors/contributors';
import { selectedTeamWithExtraDataSelector } from 'state/selectors/teams';
import { dateRangeSelector } from 'state/selectors/ui-state';

/**
 * A custom hook that filters and returns active contributors based on a selected team and date range.
 * The hook allows you to specify a buffer in months to include contributors who were active before the `from` date.
 * @param {number} [includePriorMonthsNumber] - The number of months to subtract from the `from` date for filtering contributors. default is 3.
 * @returns {Array<object>} - An array of filtered and ordered contributors who were active on or after the adjusted `from` date.
 * @example
 * const activeContributors = useActiveContributors(2);
 * // Returns contributors active on or after the date two months before the 'from' date.
 */
const useActiveContributors = (includePriorMonthsNumber = 3) => {
  const selectedTeams = useSelector(selectedTeamWithExtraDataSelector);
  const contributors = useSelector(contributorsSelector);
  const currentTeam = selectedTeams?.[0];
  const { from } = useSelector(dateRangeSelector);

  // If old All Teams (org level) or All Contributors - return no Contributors
  const teamContributors = currentTeam
    ? getTreeTeamsContributors({
        teams: [currentTeam],
        contributors
      }).filter(Boolean)
    : [];

  if (teamContributors.length === 0) {
    return [];
  }

  const teamContributorsOrdered = orderBy(teamContributors, item =>
    item?.name?.toLowerCase()
  );

  const fromDate = new Date(from);
  fromDate.setMonth(fromDate.getMonth() - includePriorMonthsNumber);

  return teamContributorsOrdered?.filter(({ last_activity_at }) => {
    const lastActivityDate = new Date(last_activity_at);
    return lastActivityDate >= fromDate;
  });
};

export default useActiveContributors;
